/**
 * Year-end (bokslut) service — thin Axios wrapper for the backend
 * /:companyId/year-end/* endpoints (accounting/year_end module).
 *
 * Mirrors the pattern of other service modules in src/services/ (see
 * InboxService.js): pure functions that take a companyId + payload and
 * return the parsed response body. Callers (components) own loading
 * state and error handling — notably the 409 gate violations the
 * backend uses for "step not ready" (surface err.response.data.message
 * to the user).
 *
 * Convention: every endpoint goes through AxiosInstance so the JWT
 * cookie and base URL are picked up uniformly.
 */

import axios from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';

const config = { withCredentials: true };

/**
 * GET /:companyId/accounting/fiscalyears
 * Returns the raw fiscal-year list (same endpoint the reports and the
 * asset tax step use). Response body: { fiscalYears: [...] }.
 */
export async function listFiscalYears(companyId) {
  const url = getApiUrl(`${companyId}/accounting/fiscalyears`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * GET /:companyId/year-end/:fiscalYearId
 * Overview payload: { success, data: { process|null, steps|null,
 * readiness: {...}, checklist: [items] } }.
 */
export async function getOverview(companyId, fiscalYearId) {
  const url = getApiUrl(`${companyId}/year-end/${fiscalYearId}`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * GET /:companyId/year-end/:fiscalYearId/readiness
 * Re-runs the readiness checks. Response body:
 * { success, data: { fiscalYear, ready, checks: [...] } }.
 */
export async function getReadiness(companyId, fiscalYearId) {
  const url = getApiUrl(`${companyId}/year-end/${fiscalYearId}/readiness`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * POST /:companyId/year-end/:fiscalYearId/start
 * Creates (or returns) the year-end process for the fiscal year.
 * Response body: { success, data: { process, created } } (201 on create).
 */
export async function startProcess(companyId, fiscalYearId, eligibilityAssessment) {
  const url = getApiUrl(`${companyId}/year-end/${fiscalYearId}/start`);
  const { data } = await axios.post(url, { eligibilityAssessment }, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/checklist/refresh
 * (Re)generates the reconciliation checklist. Response body:
 * { success, data: [items] }.
 */
export async function refreshChecklist(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/checklist/refresh`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * POST /:companyId/year-end/checklist/:itemId/attest  { note? }
 * Marks a checklist row as attested by the user.
 */
export async function attestChecklistItem(companyId, itemId, note) {
  const url = getApiUrl(`${companyId}/year-end/checklist/${itemId}/attest`);
  const body = note != null && note !== '' ? { note } : {};
  const { data } = await axios.post(url, body, config);
  return data;
}

/**
 * DELETE /:companyId/year-end/checklist/:itemId/attest
 * Removes the attestation from a checklist row.
 */
export async function unattestChecklistItem(companyId, itemId) {
  const url = getApiUrl(`${companyId}/year-end/checklist/${itemId}/attest`);
  const { data } = await axios.delete(url, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/adjustments/propose  { type, params }
 * Builds (never books) a bokslutshändelse proposal. Response body:
 * { success, data: { date, description, explanation, transactions } } —
 * for semesterloneskuld instead { ..., semesterAdjustment, delegated }.
 * Errors: 400 missing params, 409 no adjustment needed, 422 unknown
 * account — `message` is user-facing Swedish, show it verbatim.
 */
export async function proposeAdjustment(companyId, processId, type, params) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/adjustments/propose`);
  const { data } = await axios.post(url, { type, params }, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/adjustments  { type, params }
 * Books the adjustment (201). Response body:
 * { success, data: { booked, verificationId, proposal } }
 * (semesterloneskuld: { booked, result }).
 */
export async function bookAdjustment(companyId, processId, type, params) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/adjustments`);
  const { data } = await axios.post(url, { type, params }, config);
  return data;
}

/**
 * GET /:companyId/year-end/process/:processId/adjustments
 * Booked bokslutshändelser. Response body: { success, data: [
 * { adjustment_id, adjustment_type, explanation, verification_id,
 *   verification_number, series, reversed, created_at }] }.
 */
export async function listAdjustments(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/adjustments`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * Fakturor daterade efter balansdagen — periodiserings-kandidater.
 * Response body: { success, data: { balansdag, supplierInvoices: [...],
 * customerInvoices: [...] } }.
 */
export async function getAdjustmentCandidates(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/adjustments/candidates`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * DELETE /:companyId/year-end/adjustments/:adjustmentId
 * Undoes a booked adjustment via an annulment (rättelse) verifikat —
 * nothing is deleted. 409 if already reversed.
 */
export async function undoAdjustment(companyId, adjustmentId) {
  const url = getApiUrl(`${companyId}/year-end/adjustments/${adjustmentId}`);
  const { data } = await axios.delete(url, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/tax/compute
 * { pfondAvsattning?, aterforingFundYears?: number[], underskottPrevYear? }
 * Computes (never books) the year's tax and dispositions. Response body:
 * { success, data: { beskattningsar, fiscalYear, computation,
 *   schablonintaktBasis: { openingFundTotal, slrPct },
 *   funds: { opening, atYearEnd, forcedReversalYears, reversals },
 *   booked: null | {...} } } — `booked` non-null means the tax chain is
 * already in the books (start the UI in booked state).
 */
export async function computeTax(companyId, processId, body = {}) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/tax/compute`);
  const { data } = await axios.post(url, body, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/tax/book  (same body)
 * Books the tax chain (201). Response body:
 * { success, data: { booked, computation, verifications } } where
 * `verifications` may contain aterforing/avsattning/skatt/resultat ids.
 * Errors: 409 already booked, 422 avsättning over cap / missing account —
 * `message` is user-facing Swedish, show it verbatim.
 */
export async function bookTax(companyId, processId, body = {}) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/tax/book`);
  const { data } = await axios.post(url, body, config);
  return data;
}

/**
 * DELETE /:companyId/year-end/process/:processId/tax
 * Undoes the booked tax chain (rättelse verifikat — nothing is deleted).
 * 409 with a Swedish message if the year is locked or nothing is booked.
 */
export async function undoTax(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/tax`);
  const { data } = await axios.delete(url, config);
  return data;
}

/**
 * Blob-response error bodies are JSON serialized as a Blob — recover the
 * backend's Swedish `message` so callers can show it verbatim.
 */
async function rethrowBlobError(err) {
  const blob = err.response?.data;
  if (blob instanceof Blob) {
    try {
      const parsed = JSON.parse(await blob.text());
      if (parsed?.message) err.response.data = parsed;
    } catch (e) { /* keep the original error */ }
  }
  throw err;
}

/**
 * Fetches a PDF endpoint (bearer-authed, so no direct <a href>) and opens
 * it in a new tab via a blob: object URL — the same mechanism as the VAT
 * report download (SubmissionStep), but inline instead of attachment.
 */
async function openPdfInNewTab(url) {
  try {
    const resp = await axios.get(url, { ...config, responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(
      new Blob([resp.data], { type: 'application/pdf' })
    );
    window.open(blobUrl, '_blank');
    // Give the new tab time to load the blob before revoking.
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    await rethrowBlobError(err);
  }
}

/**
 * GET /:companyId/year-end/process/:processId/annual-report
 * Returns { success, data: { report, suggestions } }. `report` is the
 * annual_reports contract row (status draft → ready_for_signing → signed
 * → stamma_held → completed); `suggestions` carries the server-computed
 * prefills (resultatdisposition basis, medelantalAnstallda).
 */
export async function getAnnualReport(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/annual-report`);
  const { data } = await axios.get(url, config);
  return data;
}

/** Hela dokumentmodellen (samma som PDF:en trycker) — för dokumentvyn i ÅR-steget. */
export async function getAnnualReportRenderModel(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/annual-report/render-model`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * PUT /:companyId/year-end/process/:processId/annual-report
 * Patches prose/decision fields ({ fb_sections, notes, board_members,
 * resultatdisposition, signature_location, signature_date }) — numbers are
 * always rendered live server-side. 409 with a Swedish message once the
 * report is signed.
 */
export async function updateAnnualReport(companyId, processId, patch) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/annual-report`);
  const { data } = await axios.put(url, patch, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/annual-report/finalize
 * Content-completeness gate → status ready_for_signing. 409 with
 * { message, details: { problems: [...] } } when incomplete.
 */
export async function finalizeAnnualReport(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/annual-report/finalize`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * GET /:companyId/year-end/process/:processId/annual-report/pdf
 * Opens the rendered årsredovisning in a new tab.
 */
export async function openAnnualReportPdf(companyId, processId) {
  await openPdfInNewTab(
    getApiUrl(`${companyId}/year-end/process/${processId}/annual-report/pdf`)
  );
}

/**
 * Laddar ner årsredovisningen med SERVERNS filnamn ("Årsredovisning
 * <Företag> <org.nr> <datum>.pdf") — en blob-flik sparar annars som
 * objekt-URL:ens UUID (användarfynd 2026-08-12). Används där användaren
 * ska LÄMNA IN eller skriva ut dokumentet; förhandsgranskning öppnar
 * fortsatt i ny flik.
 */
export async function downloadAnnualReportPdf(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/annual-report/pdf`);
  try {
    const resp = await axios.get(url, { ...config, responseType: 'blob' });
    const disposition = resp.headers?.['content-disposition'] || '';
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiMatch = disposition.match(/filename="([^"]+)"/i);
    const filename = utf8Match
      ? decodeURIComponent(utf8Match[1])
      : (asciiMatch ? asciiMatch[1] : 'arsredovisning.pdf');
    const blobUrl = window.URL.createObjectURL(
      new Blob([resp.data], { type: 'application/pdf' })
    );
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    return filename;
  } catch (err) {
    await rethrowBlobError(err);
    return null;
  }
}

/**
 * GET /:companyId/year-end/process/:processId/k1-arsbokslut
 * Öppnar det förenklade årsbokslutet (K1) i en ny flik. Dokumentet ska
 * skrivas ut, undertecknas och arkiveras — det lämnas ALDRIG in.
 * Endast processtypen ef_forenklat; annars svarar servern 409.
 */
export async function openK1ArsbokslutPdf(companyId, processId) {
  await openPdfInNewTab(
    getApiUrl(`${companyId}/year-end/process/${processId}/k1-arsbokslut`)
  );
}

/**
 * GET /:companyId/year-end/process/:processId/ef-arsbokslut
 * Öppnar den enskilda firmans vanliga årsbokslut (BFNAR 2017:3) i en ny
 * flik. Samma regim som K1-dokumentet: skrivs ut, undertecknas och
 * arkiveras — lämnas ALDRIG in (det är NE-bilagan som lämnas in).
 * Endast processtypen ef_arsbokslut; annars svarar servern 409.
 */
export async function openEfArsbokslutPdf(companyId, processId) {
  await openPdfInNewTab(
    getApiUrl(`${companyId}/year-end/process/${processId}/ef-arsbokslut`)
  );
}

/**
 * POST /:companyId/year-end/process/:processId/signatures
 * { signatureDate, signatureLocation } — the print-sign path: confirms all
 * board members (+ ev. VD) signed the printed ÅR; status → signed.
 * 409 if the report isn't finalized yet.
 */
export async function recordSignatures(companyId, processId, body) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/signatures`);
  const { data } = await axios.post(url, body, config);
  return data;
}

/**
 * DELETE /:companyId/year-end/process/:processId/signatures
 * Undoes the signature confirmation (409 if the stämma is recorded).
 */
export async function undoSignatures(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/signatures`);
  const { data } = await axios.delete(url, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/stamma
 * { stammaDate, decisions: { faststallde, ansvarsfrihet, ordforande,
 *   protokollforare, justerare, resultatdisposition } } → { success,
 * data: { report, warnings: [...] } }. 422 if faststallde !== true.
 */
export async function recordStamma(companyId, processId, body) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/stamma`);
  const { data } = await axios.post(url, body, config);
  return data;
}

/**
 * DELETE /:companyId/year-end/process/:processId/stamma
 * Undoes the stämma registration (409 if the disposition is booked).
 */
export async function undoStamma(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/stamma`);
  const { data } = await axios.delete(url, config);
  return data;
}

/**
 * GET /:companyId/year-end/process/:processId/stamma/protokoll-pdf
 * Opens the årsstämmoprotokoll in a new tab.
 */
export async function openStammaProtokollPdf(companyId, processId) {
  await openPdfInNewTab(
    getApiUrl(`${companyId}/year-end/process/${processId}/stamma/protokoll-pdf`)
  );
}

/**
 * GET /:companyId/year-end/process/:processId/post-stamma
 * Step 9 status: { success, data: { currentFiscalYear,
 * nextFiscalYear: null | { id, year, start_date, end_date, state },
 * report } }.
 */
export async function getPostStamma(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/post-stamma`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/post-stamma/book-disposition
 * Books the stämma's resultatdisposition (moves årets resultat off 2099;
 * utdelning → 2898). Response: { success, data: { booked, verification,
 * aretsResultat, utdelning } }. 409/422 carry user-facing Swedish messages
 * (stämma missing, already booked, no fiscal year covering the stämma
 * date) — show them verbatim.
 */
export async function bookDisposition(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/post-stamma/book-disposition`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/complete-step/:step
 * Marks a wizard step as done. The backend gates with 409
 * ({ success:false, message, details }) when the step's requirements
 * aren't met — callers show `message` to the user.
 */
export async function completeStep(companyId, processId, step) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/complete-step/${step}`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * DELETE /:companyId/year-end/process/:processId/complete-step/:step
 * Undoes a completed step (the VAT-stepper undo semantics).
 */
export async function undoCompleteStep(companyId, processId, step) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/complete-step/${step}`);
  const { data } = await axios.delete(url, config);
  return data;
}

// ── P4: Deklaration (INK2/SRU) ───────────────────────────────────────────────

/**
 * GET /:companyId/year-end/process/:processId/ink2
 * INK2/INK2R/INK2S preview + SRU coverage. bitratt/revision are tri-state
 * (true/false/null) and echo into the preview's kryss fields.
 */
export async function getInk2Preview(companyId, processId, {
  bitratt, revision, restitueradSkatt8930, ovrigaSkatter8980,
  lanFranAktieagare, schablonintaktFondandelar,
} = {}) {
  const params = new URLSearchParams();
  if (bitratt != null) params.set('bitratt', bitratt ? '1' : '0');
  if (revision != null) params.set('revision', revision ? '1' : '0');
  if (restitueradSkatt8930) params.set('restitueradSkatt8930', restitueradSkatt8930);
  if (ovrigaSkatter8980) params.set('ovrigaSkatter8980', ovrigaSkatter8980);
  if (lanFranAktieagare != null && lanFranAktieagare !== '') params.set('lanFranAktieagare', lanFranAktieagare);
  if (schablonintaktFondandelar != null && schablonintaktFondandelar !== '') params.set('schablonintaktFondandelar', schablonintaktFondandelar);
  const qs = params.toString();
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ink2${qs ? `?${qs}` : ''}`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * POST /:companyId/year-end/process/:processId/ink2/generate
 * Returns { files: [{filename, contentBase64}], ... }. 422 when the
 * coverage gate or the biträtt/revision questions block generation.
 */
export async function generateInk2Files(companyId, processId, {
  bitratt, revision, restitueradSkatt8930, ovrigaSkatter8980,
  lanFranAktieagare, schablonintaktFondandelar,
}) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ink2/generate`);
  const { data } = await axios.post(url, {
    bitratt, revision, restitueradSkatt8930, ovrigaSkatter8980,
    lanFranAktieagare, schablonintaktFondandelar,
  }, config);
  return data;
}

/**
 * PUT /:companyId/year-end/sru-override/:accountNumber  { sruCode|null }
 * The guided coverage-gate action — validates the code against the catalog.
 */
export async function setSruOverride(companyId, accountNumber, sruCode) {
  const url = getApiUrl(`${companyId}/year-end/sru-override/${accountNumber}`);
  const { data } = await axios.put(url, { sruCode }, config);
  return data;
}

/**
 * GET /:companyId/year-end/process/:processId/statements
 * Granska-RR/BR: ÅR-classified income statement + balance sheet with
 * jämförelseår. Read-only — corrections go through verifikat.
 */
export async function getStatements(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/statements`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * GET /:companyId/year-end/:fiscalYearId/integrity — ledger-integrity
 * diagnosis (IB balance, resultatomföring residuals, EK continuity).
 */
export async function getIntegrity(companyId, fiscalYearId) {
  const url = getApiUrl(`${companyId}/year-end/${fiscalYearId}/integrity`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * POST /:companyId/year-end/:fiscalYearId/repair-pl-residual — books the
 * proposed rättelseverifikat (requires the year open; 409 otherwise).
 */
export async function repairPlResidual(companyId, fiscalYearId) {
  const url = getApiUrl(`${companyId}/year-end/${fiscalYearId}/repair-pl-residual`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * DELETE /:companyId/year-end/process/:processId/post-stamma/book-disposition
 * Reverses the booked disposition with a rättelseverifikat (409 when the
 * target year is locked or nothing is booked).
 */
export async function undoBookDisposition(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/post-stamma/book-disposition`);
  const { data } = await axios.delete(url, config);
  return data;
}

// ── P5: Enskild firma — årets resultat + NE-bilagan ──────────────────────────

/** GET .../process/:processId/ef-result — preview of the result transfer. */
export async function getEfResult(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ef-result`);
  const { data } = await axios.get(url, config);
  return data;
}

/** POST .../process/:processId/ef-result — books 8999 → 2019. */
export async function bookEfResult(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ef-result`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/** DELETE .../process/:processId/ef-result — counter-verifikat. */
export async function undoEfResult(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ef-result`);
  const { data } = await axios.delete(url, config);
  return data;
}

/** GET .../process/:processId/ne — NE preview (sida 1 + räkneschemat). */
export async function getNePreview(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ne`);
  const { data } = await axios.get(url, config);
  return data;
}

/** PUT .../process/:processId/ne — persist deklaration decisions. */
export async function saveNeDecisions(companyId, processId, decisions) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ne`);
  const { data } = await axios.put(url, decisions, config);
  return data;
}

/** POST .../process/:processId/ne/generate — info.sru + blanketter.sru. */
export async function generateNeFiles(companyId, processId, decisions) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ne/generate`);
  const { data } = await axios.post(url, decisions || {}, config);
  return data;
}

// ── P6: handelsbolag/kommanditbolag ─────────────────────────────────────────

/** GET .../process/:processId/hb-result — fördelningen innan den bokförs. */
export async function getHbResult(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/hb-result`);
  const { data } = await axios.get(url, config);
  return data;
}

/** POST .../process/:processId/hb-result — bokför resultatet på delägarna. */
export async function bookHbResult(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/hb-result`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/** GET .../process/:processId/ink4 — INK4R + INK4S + INK4DU. */
export async function getInk4Preview(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ink4`);
  const { data } = await axios.get(url, config);
  return data;
}

/** PUT .../process/:processId/ink4 — spara bolagets deklarationsbeslut. */
export async function saveInk4Decisions(companyId, processId, decisions) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ink4`);
  const { data } = await axios.put(url, decisions, config);
  return data;
}

/** POST .../process/:processId/ink4/generate — info.sru + blanketter.sru. */
export async function generateInk4Files(companyId, processId, decisions) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/ink4/generate`);
  const { data } = await axios.post(url, decisions || {}, config);
  return data;
}

export async function getFilingEvidence(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/filing-evidence`);
  const { data } = await axios.get(url, config);
  return data;
}

// Codex-blocker 2: manuell ÅR-inlämningsbekräftelse till Bolagsverket
// (ÅRL 8:3) — krävs innan bolagsverket-steget kan markeras klart.
export async function confirmBolagsverketFiling(companyId, processId, confirmation) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/bolagsverket/confirm`);
  const { data } = await axios.post(url, confirmation, config);
  return data;
}

export async function confirmFilingSubmission(companyId, processId, confirmation) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/filing-submission`);
  const { data } = await axios.post(url, confirmation, config);
  return data;
}

/** GET .../process/:processId/n3a-underlag — vad varje delägare behöver. */
export async function getN3aUnderlag(companyId, processId) {
  const url = getApiUrl(`${companyId}/year-end/process/${processId}/n3a-underlag`);
  const { data } = await axios.get(url, config);
  return data;
}

/** Öppnar HB:s årsbokslut (BFNAR 2017:3) i en ny flik. */
export async function openHbArsbokslutPdf(companyId, processId) {
  await openPdfInNewTab(
    getApiUrl(`${companyId}/year-end/process/${processId}/hb-arsbokslut`)
  );
}

// ── P6: delägarregistret ────────────────────────────────────────────────────

export async function listDelagare(companyId, fiscalYearId) {
  const q = fiscalYearId ? `?fiscalYearId=${encodeURIComponent(fiscalYearId)}` : '';
  const url = getApiUrl(`${companyId}/year-end/hb-delagare${q}`);
  const { data } = await axios.get(url, config);
  return data;
}

export async function createDelagare(companyId, body) {
  const url = getApiUrl(`${companyId}/year-end/hb-delagare`);
  const { data } = await axios.post(url, body, config);
  return data;
}

export async function updateDelagare(companyId, delagareId, body) {
  const url = getApiUrl(`${companyId}/year-end/hb-delagare/${delagareId}`);
  const { data } = await axios.put(url, body, config);
  return data;
}

export async function deleteDelagare(companyId, delagareId) {
  const url = getApiUrl(`${companyId}/year-end/hb-delagare/${delagareId}`);
  const { data } = await axios.delete(url, config);
  return data;
}

export async function saveDelagareArsuppgifter(companyId, delagareId, fiscalYearId, body) {
  const url = getApiUrl(`${companyId}/year-end/hb-delagare/${delagareId}/arsuppgifter/${fiscalYearId}`);
  const { data } = await axios.put(url, body, config);
  return data;
}
